import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, FileText, Search, Cpu,
  Eye, Pencil, Send, EyeOff, Trash2, ExternalLink, Undo2,
  RotateCcw, X, CheckSquare, Square,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const STORY_STATUS = {
  draft:       { colour: 'bg-slate-100 text-slate-600 border-slate-300',        label: 'Draft' },
  review:      { colour: 'bg-amber-50 text-amber-700 border-amber-200',         label: 'Review' },
  approved:    { colour: 'bg-blue-50 text-blue-700 border-blue-200',            label: 'Approved' },
  refused:     { colour: 'bg-red-50 text-red-600 border-red-200',               label: 'Refused' },
  published:   { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',   label: 'Published' },
  unpublished: { colour: 'bg-slate-100 text-slate-500 border-slate-300',        label: 'Unpublished' },
};

const STATUS_PILLS = [
  { id: 'all',                label: 'All' },
  { id: 'draft,review',       label: 'Review' },
  { id: 'published',          label: 'Published' },
  { id: 'unpublished,review', label: 'Unpublished' },
  { id: '__deleted__',        label: 'Deleted' },
];

function buildLiveUrl(story, pubDomain) {
  if (!pubDomain || !story.slug) return null;
  const tag = Array.isArray(story.tags) && story.tags[0]
    ? story.tags[0].toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${pubDomain}/${tag}/${story.slug}`;
}


function StoryRow({
  story, models, pubDomain, deleted, selected, onToggleSelect,
  onOpen, onPublish, onReview, onUnpublish, onDelete, onRestore, onPurge,
}) {
  const [busy, setBusy] = useState(false);
  const cfg        = STORY_STATUS[story.status] || STORY_STATUS.draft;
  const modelLabel = story.ai_model_used
    ? (models.find(m => m.id === story.ai_model_used)?.label || story.ai_model_used)
    : null;
  const isPublished = story.status === 'published';
  const liveUrl     = buildLiveUrl(story, pubDomain);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <div className={`bg-white border rounded-xl p-4 transition-colors shadow-sm ${
      selected ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={() => onToggleSelect(story.id)}
            className="mt-0.5 text-slate-400 hover:text-blue-600 transition-colors shrink-0"
            title={selected ? 'Deselect' : 'Select'}
          >
            {selected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
          </button>

          {story.image && (
            <img
              src={story.image}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="w-16 h-12 object-cover rounded shrink-0 bg-slate-100"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <FileText size={13} className="text-slate-400 shrink-0" />
              <h3 className="text-slate-900 font-medium leading-snug truncate">{story.headline}</h3>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.colour} shrink-0`}>
                {cfg.label}
              </span>
              {modelLabel && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 px-1.5 py-0.5 bg-slate-50 rounded border border-slate-200 shrink-0">
                  <Cpu size={10} className="text-slate-400" />
                  {modelLabel}
                </span>
              )}
            </div>
            {story.lede && <p className="text-xs text-slate-500 line-clamp-2 mt-1">{story.lede}</p>}
            <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400 flex-wrap">
              {story.byline_name && <span>By {story.byline_name}</span>}
              {story.source_name && <span>Source: {story.source_name}</span>}
              {story.generated_at && <span>Generated {new Date(story.generated_at).toLocaleString()}</span>}
              {story.published_at && <span className="text-emerald-600">Published {new Date(story.published_at).toLocaleString()}</span>}
              {deleted && story.deleted_at && (
                <span className="text-red-500">Deleted {new Date(story.deleted_at).toLocaleString()}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {deleted ? (
            <>
              <button onClick={() => run(() => onRestore(story))} disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                title="Restore">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                Restore
              </button>
              <button onClick={() => run(() => onPurge(story))} disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Delete permanently">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Delete forever
              </button>
            </>
          ) : (
            <>
              {isPublished && liveUrl && (
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                  title="View live">
                  <ExternalLink size={14} />
                </a>
              )}
              <button onClick={() => onOpen(story, 'view')}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                title="View details">
                <Eye size={14} />
              </button>
              <button onClick={() => onOpen(story, 'edit')}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                title="Edit story">
                <Pencil size={14} />
              </button>
              {isPublished ? (
                <>
                  <button onClick={() => run(() => onReview(story))} disabled={busy}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                    title="Send back to review">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                    Review
                  </button>
                  <button onClick={() => run(() => onUnpublish(story))} disabled={busy}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
                    title="Unpublish">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />}
                    Unpublish
                  </button>
                </>
              ) : (
                <button onClick={() => run(() => onPublish(story))} disabled={busy}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors disabled:opacity-50"
                  title="Publish">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Publish
                </button>
              )}
              <button onClick={() => run(() => onDelete(story))} disabled={busy}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Delete">
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export default function PublicationStoriesTab({ pubId, pubDomain }) {
  const navigate = useNavigate();
  const [stories, setStories]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sort, setSort]         = useState('published_desc');
  const [offset, setOffset]     = useState(0);
  const [sources, setSources]   = useState([]);
  const [models, setModels]     = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const limit = 25;

  const isDeleted = statusFilter === '__deleted__';

  useEffect(() => { setOffset(0); setSelected(new Set()); }, [search, statusFilter, sourceFilter, sort]);
  useEffect(() => { load(); }, [search, statusFilter, sourceFilter, sort, offset, pubId]);
  useEffect(() => { loadSources(); loadModels(); }, [pubId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, offset, sort };
      if (search)       params.search = search;
      if (sourceFilter) params.source_id = sourceFilter;
      if (isDeleted) {
        params.deleted = true;
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const { data } = await api.get(`/feeds/publications/${pubId}/stories`, { params });
      setStories(data.stories || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      const { data } = await api.get(`/feeds/publications/${pubId}/subscriptions`);
      const unique = [];
      const seen = new Set();
      (data || []).forEach(s => {
        if (!seen.has(s.feed_source_id)) {
          seen.add(s.feed_source_id);
          unique.push({ id: s.feed_source_id, name: s.source_name });
        }
      });
      setSources(unique);
    } catch {
    }
  };

  const loadModels = async () => {
    try {
      const { data } = await api.get('/feeds/models');
      setModels(data.models || []);
    } catch {
    }
  };

  const openStory = (story, mode = 'view') => {
    if (!story.feed_item_id) {
      toast.error('No source item linked to this story');
      return;
    }
    const params = new URLSearchParams({ tab: 'generated' });
    if (mode === 'edit') params.set('edit', story.id);
    navigate(`/feeds/items/${story.feed_item_id}?${params.toString()}`);
  };

  const handlePublish = async (story) => {
    try {
      await api.post(`/feeds/stories/${story.id}/publish`);
      toast.success('Story published');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleReview = async (story) => {
    try {
      await api.post(`/feeds/stories/${story.id}/review`);
      toast.success('Sent to review');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send to review');
    }
  };

  const handleUnpublish = async (story) => {
    try {
      await api.post(`/feeds/stories/${story.id}/unpublish`);
      toast.success('Story unpublished');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    }
  };

  const handleDelete = async (story) => {
    try {
      await api.delete(`/feeds/stories/${story.id}`);
      toast.success('Story moved to Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestore = async (story) => {
    try {
      await api.post(`/feeds/stories/${story.id}/restore`);
      toast.success('Story restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePurge = async (story) => {
    if (!window.confirm(`Permanently delete "${story.headline}"? This cannot be undone and the source article will not be regenerated.`)) return;
    try {
      await api.delete(`/feeds/stories/${story.id}/permanent`);
      toast.success('Story permanently deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete permanently');
    }
  };


  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = stories.length > 0 && stories.every(s => selected.has(s.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(stories.map(s => s.id)));
    }
  };

  const runBulk = async (label, fn) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await fn(id); ok += 1; } catch { fail += 1; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    if (fail === 0) toast.success(`${label} ${ok} ${ok === 1 ? 'story' : 'stories'}`);
    else toast.warning(`${label} ${ok}, failed ${fail}`);
    load();
  };

  const bulkUnpublish = () => runBulk('Unpublished', (id) => api.post(`/feeds/stories/${id}/unpublish`));
  const bulkDelete    = () => runBulk('Deleted',     (id) => api.delete(`/feeds/stories/${id}`));
  const bulkRestore   = () => runBulk('Restored',    (id) => api.post(`/feeds/stories/${id}/restore`));
  const bulkPurge     = () => {
    if (!window.confirm(`Permanently delete ${selected.size} ${selected.size === 1 ? 'story' : 'stories'}? This cannot be undone.`)) return;
    runBulk('Permanently deleted', (id) => api.delete(`/feeds/stories/${id}/permanent`));
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search stories..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="">All sources</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="published_desc">Newest by site published date</option>
          <option value="published_asc">Oldest by site published date</option>
          <option value="source_desc">Newest by source date</option>
          <option value="source_asc">Oldest by source date</option>
        </select>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 overflow-x-auto">
          {STATUS_PILLS.map(t => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === t.id
                  ? (t.id === '__deleted__' ? 'bg-white text-red-600 border border-slate-200 shadow-sm' : 'bg-white text-blue-600 border border-slate-200 shadow-sm')
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 bg-slate-900 text-white rounded-lg shadow-sm sticky top-2 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(new Set())} className="text-slate-300 hover:text-white transition-colors">
              <X size={16} />
            </button>
            <span className="text-sm font-medium">{selected.size} selected</span>
          </div>
          <div className="flex items-center gap-2">
            {isDeleted ? (
              <>
                <button onClick={bulkRestore} disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-xs font-medium transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore
                </button>
                <button onClick={bulkPurge} disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs font-medium transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete forever
                </button>
              </>
            ) : (
              <>
                <button onClick={bulkUnpublish} disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-xs font-medium transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />} Unpublish
                </button>
                <button onClick={bulkDelete} disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs font-medium transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading stories...
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          {isDeleted
            ? 'No deleted stories.'
            : (search || statusFilter !== 'all' || sourceFilter
                ? 'No stories match your filters.'
                : 'No feed-generated stories yet for this publication. Generate from the Feed tab.')}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-400">
              Showing {offset + 1}–{Math.min(offset + stories.length, total)} of {total}
            </p>
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors"
            >
              {allSelected ? <CheckSquare size={13} className="text-blue-600" /> : <Square size={13} />}
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="space-y-2">
            {stories.map(s => (
              <StoryRow
                key={s.output_id}
                story={s}
                models={models}
                pubDomain={pubDomain}
                deleted={isDeleted}
                selected={selected.has(s.id)}
                onToggleSelect={toggleSelect}
                onOpen={openStory}
                onPublish={handlePublish}
                onReview={handleReview}
                onUnpublish={handleUnpublish}
                onDelete={handleDelete}
                onRestore={handleRestore}
                onPurge={handlePurge}
              />
            ))}
          </div>
          {total > limit && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}